import React from 'react';
import * as Sentry from '@sentry/react';

type Props = {
  children: React.ReactNode;
  /** Telemetry only: the content item's `type`. */
  itemType?: string;
  /** When any value here changes, a previously-failed block re-attempts render
   *  (e.g. streaming finished and the item now has complete data). */
  resetKeys?: ReadonlyArray<unknown>;
};

type State = {
  hasError: boolean;
  prevResetKeys: ReadonlyArray<unknown>;
};

function shallowArrayEqual(a: ReadonlyArray<unknown>, b: ReadonlyArray<unknown>): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Per-content-item error boundary.
 *
 * A single malformed block (a tool call with an unexpected shape, a bad
 * markdown payload, etc.) used to throw during render and bubble up to the
 * GLOBAL ErrorBoundary, which replaces the entire page with the
 * "Ops, algo inesperado aconteceu" screen. This contains the failure to the
 * one block: the rest of the message and the conversation render normally,
 * and the broken block degrades to a small inline notice.
 *
 * Intentionally generic — it makes NO assumption about which item type failed
 * or why, so it protects every block uniformly (not tied to message_composer
 * or its `versions` shape).
 */
class ContentItemErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, prevResetKeys: this.props.resetKeys ?? [] };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    const keys = props.resetKeys ?? [];
    if (!shallowArrayEqual(keys, state.prevResetKeys)) {
      // resetKeys changed: clear any prior error and re-attempt render.
      return { hasError: false, prevResetKeys: keys };
    }
    return null;
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Report as a HANDLED warning so it is distinguishable from a real,
    // page-level crash (which the global ErrorBoundary tags as react-render).
    Sentry.captureException(error, {
      level: 'warning',
      tags: {
        boundary: 'content-item',
        handled: 'true',
        itemType: this.props.itemType ?? 'unknown',
      },
      contexts: { react: { componentStack: info.componentStack } },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={fallbackStyle} title="Conteúdo malformado">
          Não foi possível exibir este conteúdo.
        </div>
      );
    }
    return this.props.children;
  }
}

const fallbackStyle: React.CSSProperties = {
  margin: '8px 0',
  padding: '8px 12px',
  border: '1px dashed currentColor',
  borderRadius: 8,
  fontSize: 13,
  opacity: 0.5,
  fontStyle: 'italic',
};

export default ContentItemErrorBoundary;
